import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../css/clinical.module.css';
import '../css/pirogov-dev.css';

// ── Разделённые модули ──
import { captions, bodyOptions, modeOptions, instructions, MAX_POLL_COUNT } from './pirogov-dev/constants';
import { getCookie, setCookieValue, downloadFile } from './pirogov-dev/utils';
import useDualCtrlNav from './pirogov-dev/useDualCtrlNav';
import {
  TypewriterText,
  UserBubble, AiBubble, DownloadBtn,
  InstructionsModal, BodySwitchModal,
} from './pirogov-dev/components';
import trashIcon from '../img/agent/trash.svg';
import plusIcon from '../img/agent/plus.svg';
import maleIcon from '../img/agent/male.svg';
import femaleIcon from '../img/agent/female.svg';
import menuIcon from '../img/agent/menu.svg';

// ── Приветственное сообщение (без пункта о редактировании) ──
const WelcomeMessageSimple = () => (
  <div className="pd-welcome">
    <div className="pd-welcome__bubble">
      <div className="pd-welcome__greeting">Привет! 👋</div>
      <div>Я — AI Brain, ваш помощник для создания анатомических 3D-сцен на базе стола «Пирогов».</div>
      <div>Вот что я умею:</div>
      <ul className="pd-welcome__list">
        <li>Автоматически понимать контекст запросов: пространственная визуализация, причинно-следственная связь или коридор доступа к структуре</li>
        <li>Создавать анатомические сцены по текстовому описанию и аргументировать фокус</li>
      </ul>
      <div className="pd-welcome__hint">Опишите, что хотите увидеть, и я создам сцену для вас.</div>
    </div>
  </div>
);

const AnatomyAssistantPirogovSimple = () => {
  const [query, setQuery] = useState(() => '');
  const [selectedBody, setSelectedBody] = useState('structures.json');
  const [selectedMode, setSelectedMode] = useState(modeOptions[0].value);
  const [showBodySwitchConfirm, setShowBodySwitchConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [scenePlanningInfo, setScenePlanningInfo] = useState(null);
  const [error, setError] = useState(null);
  const [captionIndex, setCaptionIndex] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [disabledColors, setDisabledColors] = useState(false); // kept for API compat, always false
  const [apiToken, setApiToken] = useState(() => getCookie('api_token') || '');
  const [requestsRemaining, setRequestsRemaining] = useState(null);
  const [showTokenInput, setShowTokenInput] = useState(() => !getCookie('api_token'));
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [agentThoughts, setAgentThoughts] = useState([]);
  const agentThoughtsRef = useRef([]);

  const [showPlanningDetails, setShowPlanningDetails] = useState(true);
  const [savedPlanningDetails, setSavedPlanningDetails] = useState([]);
  const isSubmittingRef = useRef(false);
  const rawPresetRef = useRef(null);

  // Поле ввода для генерации новой сцены (нижняя форма)
  const [newGenQuery, setNewGenQuery] = useState('');

  // История всех завершённых генераций (для сериализации чата)
  const [generationHistory, setGenerationHistory] = useState([]);

  useEffect(() => { agentThoughtsRef.current = agentThoughts; }, [agentThoughts]);

  const [thinkingDots, setThinkingDots] = useState(1);
  const messagesEndRef = useRef(null);

  // Уточняющие вопросы
  const [clarificationQuestion, setClarificationQuestion] = useState(null);
  const [clarificationHistory, setClarificationHistory] = useState([]);
  const [clarificationAnswer, setClarificationAnswer] = useState('');
  const [isOutOfScope, setIsOutOfScope] = useState(false);
  const [chatLocked, setChatLocked] = useState(false);
  const offTopicCountRef = useRef(0);
  const clarificationOriginalQueryRef = useRef('');
  const clarificationOriginalPayloadRef = useRef(null);
  const clarificationHistoryRef = useRef([]);
  const [initialGenClarifications, setInitialGenClarifications] = useState([]);
  const sessionIdRef = useRef(null);
  // dialogue_id от бэкенда: идентификатор папки диалога, переиспользуется как chat_id
  const dialogueIdRef = useRef(null);

  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // ── История чатов (авто-сохранение без UI) ──
  const chatIdRef = useRef(null);
  const prevSaveKeyRef = useRef('');

  // ── Секретная навигация ──
  useDualCtrlNav();

  const currentInstruction = instructions[selectedMode];

  const isInChatMode = !!(downloadUrl || loading || clarificationQuestion
    || clarificationHistory.length > 0 || initialGenClarifications.length > 0 || chatLocked);

  const handleTokenSave = (token) => {
    const tokenToSave = token.trim();
    if (!tokenToSave) {
      setError('Пожалуйста, введите токен');
      return;
    }
    setCookieValue('api_token', tokenToSave);
    setApiToken(tokenToSave);
    setShowTokenInput(false);
    setError(null);

    const loadStats = async () => {
      try {
        const response = await fetch('/api/v1/token-stats/', {
          method: 'GET',
          headers: {
            'X-CSRFToken': getCookie('csrftoken'),
            'X-Auth-Token': tokenToSave,
          },
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.rate_limit && data.rate_limit.remaining !== undefined) {
            setRequestsRemaining(data.rate_limit.remaining);
          }
        } else if (response.status === 401) {
          setError('❌ Неверный токен. Проверьте и попробуйте снова.');
          setShowTokenInput(true);
          setApiToken('');
          setCookieValue('api_token', '', -1);
        } else if (response.status === 502) {
          const text = await response.text();
          console.error('❌ Server error (502):', text);
          setError('❌ Ошибка сервера (502). Проверьте логи сервера.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(`❌ Ошибка при загрузке статистики (${response.status}): ${errorData.error || 'Unknown error'}`);
        }
      } catch (err) {
        console.error('❌ Error loading token stats:', err);
        setError(`❌ Ошибка при загрузке статистики: ${err.message}`);
      }
    };

    loadStats();
  };

  const handleTokenReset = () => {
    setApiToken('');
    setCookieValue('api_token', '', -1);
    setShowTokenInput(true);
    setRequestsRemaining(null);
  };

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setCaptionIndex((prev) => (prev + 1) % captions.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setThinkingDots(d => d >= 3 ? 1 : d + 1);
      }, 500);
      return () => clearInterval(interval);
    } else {
      setThinkingDots(1);
    }
  }, [loading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [downloadUrl, loading, clarificationQuestion]);

  useEffect(() => {
    const loadTokenStats = async () => {
      if (apiToken && !requestsRemaining) {
        try {
          const response = await fetch('/api/v1/token-stats/', {
            method: 'GET',
            headers: {
              'X-CSRFToken': getCookie('csrftoken'),
              'X-Auth-Token': apiToken,
            },
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            if (data.rate_limit && data.rate_limit.remaining !== undefined) {
              setRequestsRemaining(data.rate_limit.remaining);
            }
          } else if (response.status === 401) {
            setShowTokenInput(true);
          } else if (response.status === 502) {
            const text = await response.text();
            console.error('❌ Server error (502 Bad Gateway):', text);
            setError('❌ Ошибка сервера (502). Проверьте логи на сервере.');
          } else {
            const errorData = await response.json().catch(() => ({}));
            setError(`❌ Ошибка ${response.status}: ${errorData.error || 'Unknown error'}`);
          }
        } catch (err) {
          console.error('❌ Network error loading token stats:', err);
          setError(`❌ Ошибка сети: ${err.message}`);
        }
      }
    };

    loadTokenStats();
  }, [apiToken]);

  const handleDownload = useCallback(async (url) => {
    try {
      await downloadFile(url, apiToken);
    } catch (err) {
      console.error('Download error:', err);
      setError('Ошибка при скачивании файла.');
    }
  }, [apiToken]);

  const pollCountRef = useRef(0);

  // R8: единая точка захвата идентификаторов диалога/сессии из ответов
  // сервера (parity с dev-страницей): poll-ответы несут data.session_id /
  // data.result.dialogue_id, sync-фолбэк — data.dialogue_id.
  const captureIds = (data) => {
    if (data.session_id) sessionIdRef.current = data.session_id;
    const did = data.result?.dialogue_id || data.dialogue_id;
    if (did) dialogueIdRef.current = did;
  };

  const pollTaskStatus = (taskId) => {
    return new Promise((resolve) => {
      const poll = async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLL_COUNT) {
          setError('Превышено время ожидания ответа. Попробуйте ещё раз.');
          setLoading(false);
          setCurrentPhase(null);
          setCurrentAgent(null);
          resolve();
          return;
        }

        try {
          const response = await fetch(`/api/v1/task-status/${taskId}/`, {
            method: 'GET',
            headers: {
              'X-CSRFToken': getCookie('csrftoken'),
              'X-Auth-Token': apiToken,
            },
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error(`Ошибка сервера: ${response.status}`);
          }

          const data = await response.json();

          if (data.status === 'SUCCESS') {
            offTopicCountRef.current = 0;
            // Захват id до любых сохранений чата (артефакты уже в папке диалога)
            captureIds(data);
            const result = data.result;
            if (result.download_url) {
              // Сохраняем уточнения генерации
              setInitialGenClarifications([...clarificationHistoryRef.current]);
              setClarificationHistory([]);
              clarificationHistoryRef.current = [];
              setDownloadUrl(result.download_url);
              const fullScenePlanningInfo = {
                ...result.scene_planning_info,
                rationale: result.ai_response?.rationale,
                applied_mode: result.ai_response?.applied_mode,
                special_rules: result.ai_response?.special_rules,
              };
              setScenePlanningInfo(fullScenePlanningInfo);
              setSavedPlanningDetails(agentThoughtsRef.current.filter(t => t.label));
              rawPresetRef.current = result.raw_preset_filename || null;
              setAgentThoughts([]);

              if (result.rate_limit && result.rate_limit.remaining !== undefined) {
                setRequestsRemaining(result.rate_limit.remaining);
              }
              setClarificationQuestion(null);
            } else {
              setError(result.error || result.details || 'Не удалось получить ссылку для скачивания.');
            }
            setLoading(false);
            setCurrentPhase(null);
            setCurrentAgent(null);
            resolve();
          } else if (data.status === 'CLARIFICATION_NEEDED') {
            const result = data.result;
            // Уточнение остаётся в том же диалоге — захват id единым хелпером
            captureIds(data);

            if (result.is_informational) {
              setClarificationQuestion(result.clarification_question);
              setIsOutOfScope(true);
              offTopicCountRef.current = 0;
            } else if (result.is_out_of_scope) {
              offTopicCountRef.current += 1;
              setClarificationQuestion(result.clarification_question);
              setIsOutOfScope(true);
              if (offTopicCountRef.current >= 3) {
                setChatLocked(true);
              }
            } else {
              setClarificationQuestion(result.clarification_question);
              setIsOutOfScope(false);
              offTopicCountRef.current = 0;
            }
            setLoading(false);
            setCurrentPhase(null);
            setCurrentAgent(null);
            resolve();
          } else if (data.status === 'FAILURE') {
            setError(data.error || 'Произошла ошибка при обработке запроса.');
            setLoading(false);
            setCurrentPhase(null);
            setCurrentAgent(null);
            resolve();
          } else {
            // PENDING или PROCESSING
            if (data.progress) {
              if (data.progress.phase) setCurrentPhase(data.progress.phase);
              if (data.progress.agent) setCurrentAgent(data.progress.agent);
              if (Array.isArray(data.progress.thoughts) && data.progress.thoughts.length > 0) {
                setAgentThoughts(data.progress.thoughts);
              }
            }
            setTimeout(poll, 2000);
          }
        } catch (err) {
          console.error('Polling error:', err);
          setError('Ошибка при проверке статуса задачи.');
          setLoading(false);
          setCurrentPhase(null);
          setCurrentAgent(null);
          resolve();
        }
      };
      poll();
    });
  };

  // ── Общая логика генерации сцены ──
  const doGenerate = async (queryText) => {
    pollCountRef.current = 0;

    // Сохраняем текущую завершённую генерацию в историю перед сбросом
    if (downloadUrl) {
      setGenerationHistory(prev => [...prev, {
        query,
        downloadUrl,
        scenePlanningInfo,
        savedPlanningDetails,
        rawPresetFilename: rawPresetRef.current,
        clarifications: [...initialGenClarifications],
      }]);
    }

    setQuery(queryText);
    clarificationOriginalQueryRef.current = queryText;
    clarificationOriginalPayloadRef.current = {
      body: selectedBody,
      mode: selectedMode,
    };
    setClarificationHistory([]);
    clarificationHistoryRef.current = [];
    setClarificationQuestion(null);
    setIsOutOfScope(false);
    setChatLocked(false);
    offTopicCountRef.current = 0;
    sessionIdRef.current = null;
    // F3: dialogueIdRef НЕ сбрасываем — следующая генерация в том же чате
    // переиспользует диалоговую папку; сброс только при новом чате (reload)

    setLoading(true);
    setError(null);
    setDownloadUrl(null);
    setInitialGenClarifications([]);
    setScenePlanningInfo(null);
    setSavedPlanningDetails([]);
    setCurrentPhase(null);
    setCurrentAgent(null);
    setAgentThoughts([]);

    try {
      const response = await fetch('/api/v1/pirogov-query/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify({
          query: queryText,
          body: selectedBody,
          mode: selectedMode,
          session_id: sessionIdRef.current,
          dialogue_id: dialogueIdRef.current || undefined,
        }),
        credentials: 'include',
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        console.error('Server response is not JSON:', text);
        setError(`Ошибка сервера (${response.status}): ${text.substring(0, 200)}`);
        setLoading(false);
        return;
      }

      if (response.status === 401) {
        setError('❌ Неверный токен. Проверьте и попробуйте снова.');
        setShowTokenInput(true);
        setLoading(false);
      } else if (response.status === 429) {
        setError('⚠️ Лимит запросов на месяц исчерпан. Попробуйте в следующем месяце.');
        setLoading(false);
      } else if (response.status === 202 && data.task_id) {
        if (data.session_id) sessionIdRef.current = data.session_id;
        pollTaskStatus(data.task_id);
      } else if (response.ok && data.download_url) {
        // Sync fallback: dialogue_id приходит через poll, здесь — для shape parity
        captureIds(data);
        setDownloadUrl(data.download_url);
        const fullScenePlanningInfo = {
          ...data.scene_planning_info,
          rationale: data.ai_response?.rationale,
          applied_mode: data.ai_response?.applied_mode,
          special_rules: data.ai_response?.special_rules,
        };
        setScenePlanningInfo(fullScenePlanningInfo);
        if (data.rate_limit && data.rate_limit.remaining !== undefined) {
          setRequestsRemaining(data.rate_limit.remaining);
        }
        setLoading(false);
      } else {
        setError(data.error || data.details || 'Не удалось получить ссылку для скачивания.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Request error:', err);
      setError('Ошибка подключения к серверу.');
      setLoading(false);
    }
  };

  // ── Первичная генерация (верхняя форма) ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (!apiToken.trim()) {
      setError('Пожалуйста, введите API токен');
      return;
    }
    await doGenerate(query);
  };

  // ── Генерация новой сцены (нижняя форма, после готовности предыдущей) ──
  const handleNewGenSubmit = async (e) => {
    e.preventDefault();
    if (!newGenQuery.trim()) return;
    if (!apiToken.trim()) {
      setError('Пожалуйста, введите API токен');
      return;
    }
    const q = newGenQuery;
    setNewGenQuery('');
    await doGenerate(q);
  };

  // ── Отправка ответа на уточняющий вопрос ИИ ──
  const handleClarificationSubmit = async (e) => {
    e.preventDefault();
    if (!clarificationAnswer.trim()) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    pollCountRef.current = 0;

    const newHistory = [...clarificationHistory, {
      question: clarificationQuestion,
      answer: clarificationAnswer.trim(),
    }];
    clarificationHistoryRef.current = newHistory;
    setClarificationHistory(newHistory);
    setClarificationAnswer('');
    setClarificationQuestion(null);
    setIsOutOfScope(false);

    // Уточнение для генерации
    setLoading(true);
    setError(null);
    try {
      const payload = {
        query: clarificationOriginalQueryRef.current || query || undefined,
        body: clarificationOriginalPayloadRef.current?.body || selectedBody,
        mode: clarificationOriginalPayloadRef.current?.mode || selectedMode,
        clarification_history: newHistory,
        session_id: sessionIdRef.current,
        dialogue_id: dialogueIdRef.current || undefined,
      };

      const response = await fetch('/api/v1/pirogov-query/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.status === 202 && data.task_id) {
        if (data.session_id) sessionIdRef.current = data.session_id;
        pollTaskStatus(data.task_id);
      } else if (response.ok && data.download_url) {
        // Sync fallback: dialogue_id приходит через poll, здесь — для shape parity
        captureIds(data);
        setDownloadUrl(data.download_url);
        setLoading(false);
      } else {
        setError(data.error || 'Ошибка при обработке запроса');
        setLoading(false);
      }
    } catch (err) {
      console.error('Clarification generation error:', err);
      setError(err.message || 'Ошибка при отправке уточнения');
      setLoading(false);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // ── Сериализация и авто-сохранение чата ──
  const serializeChatState = () => {
    const messages = [];

    // Предыдущие завершённые генерации
    generationHistory.forEach(gen => {
      messages.push({ role: 'user', type: 'query', text: gen.query });
      gen.clarifications?.forEach(c => {
        messages.push({ role: 'assistant', type: 'clarification', text: c.question });
        messages.push({ role: 'user', type: 'clarification_answer', text: c.answer });
      });
      messages.push({
        role: 'assistant', type: 'generation',
        download_url: gen.downloadUrl,
        scene_planning_info: gen.scenePlanningInfo,
        saved_planning_details: gen.savedPlanningDetails,
        raw_preset_filename: gen.rawPresetFilename || null,
      });
    });

    // Текущая генерация
    messages.push({ role: 'user', type: 'query', text: query });

    initialGenClarifications.forEach(c => {
      messages.push({ role: 'assistant', type: 'clarification', text: c.question });
      messages.push({ role: 'user', type: 'clarification_answer', text: c.answer });
    });

    if (downloadUrl) {
      messages.push({
        role: 'assistant', type: 'generation',
        download_url: downloadUrl,
        scene_planning_info: scenePlanningInfo,
        saved_planning_details: savedPlanningDetails,
        raw_preset_filename: rawPresetRef.current || null,
      });
    }

    return messages;
  };

  const saveChat = async () => {
    if (!apiToken || !downloadUrl) return;
    try {
      const messages = serializeChatState();
      const resp = await fetch('/api/v1/chats/save/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify({
          // dialogue_id как chat_id: артефакты уже лежат в папке диалога (бэкенд пропускает raw-copy)
          chat_id: dialogueIdRef.current || chatIdRef.current || undefined,
          // Q2 parity с dev: персистим dialogue_id, чтобы загрузка из истории
          // восстановила цепочку диалога
          dialogue_id: dialogueIdRef.current || undefined,
          body: selectedBody,
          mode: selectedMode,
          title: (generationHistory.length > 0
            ? generationHistory[0].query
            : query || ''
          ).substring(0, 120),
          messages,
        }),
        credentials: 'include',
      });
      const data = await resp.json();
      if (data.success && data.chat_id) {
        chatIdRef.current = data.chat_id;
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  useEffect(() => {
    if (!downloadUrl || !apiToken || loading) return;
    const saveKey = `${downloadUrl}`;
    if (saveKey === prevSaveKeyRef.current) return;
    prevSaveKeyRef.current = saveKey;
    const timer = setTimeout(() => saveChat(), 800);
    return () => clearTimeout(timer);
  }, [downloadUrl, loading]);

  const getSceneValue = (key) => {
    if (!scenePlanningInfo) return undefined;
    if (scenePlanningInfo[key]) return scenePlanningInfo[key];
    if (scenePlanningInfo.rationale && typeof scenePlanningInfo.rationale !== 'string') {
      return scenePlanningInfo.rationale[key];
    }
    return undefined;
  };

  const planningDetails = agentThoughts.filter(t => t.label);

  return (
    <>

      {/* Instructions Modal */}
      {showInstructions && (
        <InstructionsModal
          selectedMode={selectedMode}
          onSelectMode={setSelectedMode}
          currentInstruction={currentInstruction}
          onClose={() => setShowInstructions(false)}
        />
      )}

      {/* Body Switch Confirm Modal */}
      {showBodySwitchConfirm && (
        <BodySwitchModal
          selectedBody={selectedBody}
          onClose={() => setShowBodySwitchConfirm(false)}
        />
      )}

      <div className={`pd-root${downloadUrl ? ' pd-root--chat' : ' pd-root--form'}`}>

      <div className={styles.backgroundSvg} />

        {/* Декоративные круги на фоне */}
        <div className="pd-deco-circle pd-deco-circle--top" />
        <div className="pd-deco-circle pd-deco-circle--bottom" />

        {/* Основной контейнер */}
        <div className="pd-container">

          <div className="pd-header">
            <div className="pd-header__row">
              <h1 className="pd-header__title"><a href="/" style={{ color: 'inherit', textDecoration: 'none' }}>🧠 Brain AI</a></h1>

              {/* ── Кнопка мобильного меню ── */}
              <button
                type="button"
                className="pd-menu-btn"
                onClick={() => setShowMobileMenu(prev => !prev)}
                aria-label="Меню"
              >
                <img src={menuIcon} alt="меню" className="pd-btn-icon" />
              </button>

              {/* ── Навигация шапки ── */}
              <div className={`pd-header-nav${showMobileMenu ? ' pd-header-nav--open' : ''}`}>

                {/* ── Кнопка «Новый чат» ── */}
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="pd-header-btn"
                >
                  <img src={plusIcon} alt="" className="pd-btn-icon" />
                  <span className="pd-btn-label">Новый чат</span>
                </button>

                {/* ── Кнопка ключа (в меню) ── */}
                {apiToken && (
                  <button
                    type="button"
                    onClick={() => setShowTokenInput(prev => !prev)}
                    className="pd-token-btn"
                    title="API токен"
                  >
                    🔑
                  </button>
                )}

                {/* ── Триггер Мужчина / Женщина ── */}
                <div className="pd-body-toggle">
                  {bodyOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        if (opt.value !== selectedBody) {
                          if (isInChatMode) {
                            setShowBodySwitchConfirm(true);
                          } else {
                            setSelectedBody(opt.value);
                            const params = new URLSearchParams(window.location.search);
                            params.set('body', 'structures');
                            window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
                          }
                        }
                      }}
                      className={`pd-body-toggle__btn${selectedBody === opt.value ? ' pd-body-toggle__btn--active' : ' pd-body-toggle__btn--inactive'}`}
                    >
                      <img src={maleIcon} alt="" className="pd-btn-icon" />
                      <span className="pd-btn-label">Анатомия</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* ── Оставшиеся запросы ── */}
              <div className="pd-header__right">
                {apiToken && requestsRemaining !== null && (
                  <div className="pd-requests-badge">
                    <span className="pd-requests-badge__label">Запросов осталось:</span>
                    <span className="pd-requests-badge__count">{requestsRemaining}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 🔑 Плашка для ввода/замены токена */}
            {showTokenInput && (
              <div className="pd-token-panel">
                <div className="pd-token-panel__header">
                  <span className="pd-token-panel__icon">🔑</span>
                  <div className="pd-token-panel__title">
                    API Токен {apiToken ? '(замена)' : '(обязателен)'}
                  </div>
                </div>
                <input
                  type="password"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder="Вставьте ваш API токен здесь..."
                  className="pd-token-input"
                />
                <div className="pd-token-panel__actions">
                  {apiToken && (
                    <button
                      type="button"
                      onClick={() => { handleTokenSave(apiToken); }}
                      className="pd-token-save"
                    >
                      ✓ Сохранить
                    </button>
                  )}
                  {apiToken && (
                    <button
                      type="button"
                      onClick={handleTokenReset}
                      className="pd-token-delete"
                    >
                      <img src={trashIcon} alt="trash" style={{ width: 16, height: 16 }} /> Удалить
                    </button>
                  )}
                  {!apiToken && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!apiToken.trim()) {
                          setError('Пожалуйста, введите токен');
                          return;
                        }
                        handleTokenSave(apiToken);
                      }}
                      className="pd-token-apply"
                    >
                      ✓ Применить токен
                    </button>
                  )}
                </div>
                <div className="pd-token-panel__hint">
                  💡 Токен будет сохранён в cookies браузера и не потребуется вводить заново.
                </div>
              </div>
            )}

          </div>

          {!downloadUrl && !loading && !clarificationQuestion && clarificationHistory.length === 0 && (
          <div className="pd-form-center">
          <div className="pd-form-inner">

            <WelcomeMessageSimple />

            <form onSubmit={handleSubmit}>

              <div className="pd-form__heading">
                Что показать?
              </div>

              <div className="pd-form__subheading">
                Опишите текстом, что вы хотите увидеть.
              </div>

              {/* Query Textarea */}
              <div className="pd-form__textarea-wrap">

                <div className="pd-form__textarea-relative">
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="например, внутренняя анатомия мозга"
                    rows={3}
                    disabled={!!downloadUrl}
                    className="pd-initial-textarea"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                    }}
                  />
                  <div className="pd-initial-buttons">
                    <button
                      type="submit"
                      disabled={loading || !apiToken || !!downloadUrl}
                      className="pd-initial-send"
                      title={!apiToken ? 'Сначала введите токен' : 'Сгенерировать'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                    </button>
                  </div>
                </div>

              </div>

              {/* Toggle: Разделить цветами целевые структуры — removed, colors always enabled */}

            </form>

          </div>
          </div>
          )}

          {/* Results Section - Chat Mode */}
          {(downloadUrl || loading || clarificationQuestion || clarificationHistory.length > 0 || initialGenClarifications.length > 0) && (
            <div className="pd-results">

              {/* ── Messages area ── */}
              <div className={`pd-messages${isInChatMode ? ' pd-messages--with-input' : ''}`}>

                {/* Initial generation message */}
                <div className="pd-msg-group">
                  <UserBubble>
                    {query}
                  </UserBubble>

                  {/* Уточняющие вопросы + ответы генерации — до результата */}
                  {initialGenClarifications.length > 0 && (
                    <div className="pd-msg-group">
                      {initialGenClarifications.map((entry, idx) => (
                        <React.Fragment key={`igc-${idx}`}>
                          <AiBubble text={entry.question} animate={false} />
                          <UserBubble>
                            {entry.answer}
                          </UserBubble>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {downloadUrl && (
                  <div className="pd-scene-response">

                    {savedPlanningDetails.length > 0 && (
                      <div className="pd-planning">
                        <div className="pd-planning__header" onClick={() => setShowPlanningDetails(!showPlanningDetails)}>
                          <span>Детали планирования сцены</span>
                          <span className={`pd-planning__chevron${showPlanningDetails ? ' pd-planning__chevron--open' : ''}`}>▾</span>
                        </div>
                        {showPlanningDetails && (
                          <div className="pd-planning__body">
                            {savedPlanningDetails.map((t, idx) => (
                              <div key={idx} className="pd-planning__section">
                                <div className="pd-planning__label">{t.label}</div>
                                <div className="pd-planning__value">{t.text}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pd-status-msg">✅ Сцена сгенерирована</div>

                    <DownloadBtn onClick={() => handleDownload(downloadUrl)} initial />
                  </div>
                  )}
                </div>

                {/* История уточняющих вопросов — отображается ДО «Думает...» */}
                {clarificationHistory.length > 0 && (
                  <div className="pd-msg-group">
                    {clarificationHistory.map((entry, idx) => (
                      <React.Fragment key={`clarification-${idx}`}>
                        <AiBubble text={entry.question} animate={false} />
                        <UserBubble>
                          {entry.answer}
                        </UserBubble>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Активный уточняющий вопрос от ИИ */}
                {clarificationQuestion && !loading && (
                  <AiBubble text={clarificationQuestion} animate variant="pop" />
                )}

                {/* Прогрессивные детали планирования сцены (только во время обработки) */}
                {loading && planningDetails.length > 0 && (
                  <div className="pd-planning pd-planning--progress">
                    <div className="pd-planning__header pd-planning__header--static">
                      <span>Детали планирования сцены</span>
                    </div>
                    <div className="pd-planning__body">
                      {planningDetails.map((t, idx) => (
                        <div key={idx} className="pd-planning__section">
                          <div className="pd-planning__label">{t.label}</div>
                          <div className="pd-planning__value">
                            {idx === planningDetails.length - 1 && loading
                              ? <TypewriterText text={t.text} />
                              : t.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {loading && (
                  <div className="pd-msg-left pd-thinking">
                    {currentPhase ? currentPhase + '.'.repeat(thinkingDots) : 'Думает' + '.'.repeat(thinkingDots)}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* ── Форма для генерации новой сцены (внизу, после готовности предыдущей) ── */}
              {downloadUrl && !loading && !clarificationQuestion && !chatLocked && <div className="pd-fixed-bottom">
                <form onSubmit={handleNewGenSubmit}>
                  <div className="pd-input-wrap">
                    <textarea
                      value={newGenQuery}
                      onChange={(e) => setNewGenQuery(e.target.value)}
                      placeholder="Опишите новую сцену..."
                      rows={3}
                      className="pd-chat-textarea"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (newGenQuery.trim() && apiToken.trim()) handleNewGenSubmit(e); }
                      }}
                    />
                    <div className="pd-input-buttons">
                      <button
                        type="submit"
                        disabled={!newGenQuery.trim()}
                        className="pd-send-btn"
                        title="Сгенерировать новую сцену"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="19" x2="12" y2="5" />
                          <polyline points="5 12 12 5 19 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>}

              {/* ── Панель «Чат заблокирован» ── */}
              {chatLocked && !loading && <div className="pd-fixed-bottom">
                <div className="pd-locked-msg">
                  🔒 Чат заблокирован из-за повторных нарушений правил использования.
                </div>
                <div className="pd-locked-actions">
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="pd-locked-btn"
                  >↻ Новый чат</button>
                </div>
              </div>}

              {/* ── Фиксированная панель ввода ответа на уточняющий вопрос ── */}
              {clarificationQuestion && !loading && !chatLocked && <div className="pd-fixed-bottom">
                <form onSubmit={handleClarificationSubmit}>
                  <div className="pd-input-wrap">
                    <textarea
                      value={clarificationAnswer}
                      onChange={(e) => setClarificationAnswer(e.target.value)}
                      placeholder={isOutOfScope ? 'Введите запрос об анатомии человека...' : 'Ваш ответ на уточняющий вопрос ИИ...'}
                      rows={3}
                      className="pd-chat-textarea"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (clarificationAnswer.trim()) handleClarificationSubmit(e); }
                      }}
                    />
                    <div className="pd-input-buttons">
                      <button
                        type="submit"
                        disabled={!clarificationAnswer.trim()}
                        className="pd-send-btn"
                        title={isOutOfScope ? 'Отправить новый запрос' : 'Ответить на вопрос'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="19" x2="12" y2="5" />
                          <polyline points="5 12 12 5 19 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>}

            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AnatomyAssistantPirogovSimple;
